import React from 'react'
import * as Popover from '@radix-ui/react-popover'
import { useField } from 'formik'
import { ChevronDown } from 'lucide-react'
import clsx from 'clsx'

import { WithId } from 'django-rest-react'

import { getMappings } from './Select'
import { FieldWrapper } from './FieldWrapper'
import { inputClasses } from './form-defaults'

const FormikMultiSelectCheckbox = <T extends WithId>({
  name,
  choices,
  displayChoice,
  label,
  wrapped = true,
  helpText,
  readOnly,
}: {
  name: string
  choices: T[]
  displayChoice?: (val: T) => string
  label?: string
  wrapped?: boolean
  helpText?: string
  readOnly?: boolean
}) => {
  const [field, meta, helpers] = useField(name)
  const value_base: (string | number)[] = field.value || []
  const value: string[] = value_base.map(val => val.toString())

  const [display, getOpt, restore] = getMappings(choices, displayChoice);

  const toggleValue = (val: string) => {
    const next = value.includes(val)
      ? value.filter(v => v !== val)
      : [...value, val]
    // Remember that parseInt has more than one input and calling
    // .map(restore) directly results in all NaNs
    helpers.setValue(next.map(x => restore(x)))
  }

  const displayShort =
    value.length > 0
      ? value
        .map(val => choices.find(choice => getOpt(choice) === val))
        .map((val: T | undefined) => {
          if (!val) return "Not found"
          return display(val)
        })
        .join(", ")
      : "Select options"

  const internal = (
    <div>
      <Popover.Root>
        <Popover.Trigger
          disabled={readOnly}
          className={clsx(
            "inline-flex items-center justify-between rounded-xl border px-4 py-2 w-full bg-background shadow-sm text-sm",
            inputClasses(!!readOnly, !!meta.error)
          )}
        >
          {displayShort}
          <ChevronDown className="ml-2 h-4 w-4" />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="z-50 mt-1 rounded-xl border bg-background p-2 shadow-lg w-60 max-h-60 overflow-y-auto"
          >
            {choices.map(opt => (
              <label
                key={getOpt(opt)}
                className="flex items-center space-x-2 p-2 hover:bg-primary/90 rounded-md cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(getOpt(opt))}
                  onChange={() => toggleValue(getOpt(opt))}
                  className="form-checkbox rounded text-primary accent-primary"
                />
                <span>{display(opt)}</span>
              </label>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  )

  return (
    <FieldWrapper
      name={name}
      label={label}
      helpText={helpText}
      error={meta.touched && meta.error ? meta.error : undefined}
      wrapped={wrapped}
    >
      {internal}
    </FieldWrapper>
  )
}

export default FormikMultiSelectCheckbox;
